import React, { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ClipboardTextIcon, DownloadSimpleIcon, CheckIcon } from "@phosphor-icons/react";

/* ───────────────────────────────────────────────────────────────────────── */

const PROFILE_PICS = [
  { src: "/brand/bidorah-profile-1024.png", label: "Profile · 1024 · Square", hint: "IG, TikTok, X, Facebook profile" },
  { src: "/brand/bidorah-profile-2048.png", label: "Profile · 2048 · Retina", hint: "LinkedIn, press packets" },
  { src: "/brand/bidorah-profile-circle-1024.png", label: "Profile · 1024 · Circle", hint: "Pre-cropped for Discord, Slack" },
  { src: "/brand/bidorah-profile-transparent-1024.png", label: "Mark · Transparent", hint: "For dark-bg overlays, decks" },
];

const SHARE_CARDS = [
  { src: "/brand/bidorah-og-1200x630.png", label: "OpenGraph · 1200×630", hint: "iMessage, X, Discord link previews" },
];

const MOCKUPS = [
  { src: "/brand/mockups/01_hero_square.png",      label: "01 — Hero · 1080×1080",   hint: "Instagram feed launch post" },
  { src: "/brand/mockups/02_twitter_banner.png",   label: "02 — Twitter banner · 1500×500", hint: "X / Twitter profile header" },
  { src: "/brand/mockups/03_video_thumbnail.png",  label: "03 — Video thumb · 1280×720",  hint: "YouTube + Twitter video thumbnail" },
  { src: "/brand/mockups/04_share_card.png",       label: "04 — Share card · 1200×675",   hint: "OG / LinkedIn share card · trust angle" },
  { src: "/brand/mockups/05_story_poster.png",     label: "05 — Story poster · 1080×1920", hint: "IG Story / TikTok / Snap · scarcity" },
  { src: "/brand/final-mockup.png",                label: "06 — Full launch poster",      hint: "Three-phone hero · v2 final" },
];

const POSTS = [
  { src: "/brand/ig-post-01-bid-live.png", label: "01 — Bid live. Win real.", hook: "hero" },
  { src: "/brand/ig-post-02-verified.png", label: "02 — Every seller US-verified", hook: "trust" },
  { src: "/brand/ig-post-03-founder.png", label: "03 — 250 founder seats", hook: "scarcity" },
  { src: "/brand/ig-post-04-segregation.png", label: "04 — Wholesale or retail, never mixed", hook: "moat" },
  { src: "/brand/ig-post-05-plus.png", label: "05 — Bidorah Plus", hook: "monetization" },
  { src: "/brand/ig-post-06-prices.png", label: "06 — Price tracker", hook: "utility" },
];

const STORIES = [
  { src: "/brand/ig-story-01.png", label: "Story 01 · Bid live. Win real.", hook: "hero" },
  { src: "/brand/ig-story-02.png", label: "Story 02 · 250 seats", hook: "scarcity" },
  { src: "/brand/ig-story-03.png", label: "Story 03 · Plus", hook: "Plus launch" },
];

const REELS = [
  {
    src: "/brand/reels/reel-01-bid-live.mp4",
    poster: "/brand/ig-story-01.png",
    label: "Reel 01 · Bid live. Win real.",
    hint: "15s · hero launch · with audio",
    duration: "0:15",
  },
  {
    src: "/brand/reels/reel-02-founder.mp4",
    poster: "/brand/ig-story-02.png",
    label: "Reel 02 · Founder 250",
    hint: "20s · scarcity counter · with audio",
    duration: "0:20",
  },
  {
    src: "/brand/reels/reel-03-verified.mp4",
    poster: "/brand/ig-post-02-verified.png",
    label: "Reel 03 · Every seller verified",
    hint: "15s · trust proof · with audio",
    duration: "0:15",
  },
  {
    src: "/brand/reels/reel-04-plus.mp4",
    poster: "/brand/ig-story-03.png",
    label: "Reel 04 · Bidorah Plus",
    hint: "18s · monetization · with audio",
    duration: "0:18",
  },
];

const CAPTIONS = {
  "Hero — Bid live. Win real.": {
    instagram:
      "Bid live. Win real. ⚡\n\nBidorah is live-auction commerce for people who actually collect. Every seller US-verified. Every stream real-time. Every bid transparent on-chain — okay, not on-chain, but on a ledger that's not lying to you.\n\nNo dropshippers. No flippers. No scams.\n\nComment \"DROP\" and I'll DM you the founder link.\n\n#livestreamshopping #auctions #collectors #liveshopping #sneakerheads #tcg #crystals #fragrances #bidorah #luxury #founder",
    tiktok:
      "POV: You just discovered live auctions without the chaos 💎\n\nEvery seller verified. Every bid transparent. Zero flippers. Zero dropshippers.\n\nLink in bio → bidorah.com\n\n#fyp #collectorsoftiktok #livestreamshopping #auctions #founderseller",
    x:
      "Live auction commerce, built for collectors.\n\n• Every seller US-verified\n• Real-time WebRTC streams\n• Wholesale & retail strictly segregated\n• 1% cashback for Plus members\n\nbidorah.com — join the 250.",
  },
  "Scarcity — Founder 250": {
    instagram:
      "Only 250 seats. 0% platform fees for 14 days. Founder badge for life.\n\nWe're letting in 250 \"Founder Sellers\" before Bidorah opens to the public. Retail, wholesale, reselling — doesn't matter what you sell. We want the real ones.\n\nApplications close when the 250th is filled. No extensions.\n\nLink in bio → bidorah.com/apply-to-sell\n\n#founder #liveshopping #sell #reseller #reselling #auctions #livestreamseller #sneakerreseller",
    tiktok:
      "250 seats. 0% fees for 14 days. Founder badge for life. 🏷️\n\nApplying → bidorah.com/apply-to-sell\n\n#fyp #reseller #livestreamseller #foundingmember #livestreamshopping",
    x:
      "250 Founder Seller seats. 0% fees for 14 days. Founder badge for life.\n\nNo extensions. Apply before the counter hits zero.\n\nbidorah.com/apply-to-sell",
  },
  "Trust — Every seller verified": {
    instagram:
      "\"But how do I know the seller isn't going to ghost me?\"\n\nBecause we don't let anyone sell until they've cleared:\n✓ US photo-ID verification\n✓ Real-name match on payout account\n✓ TIN + Reseller Permit (wholesale)\n✓ Admin review\n\nEvery listing on Bidorah carries a verified checkmark because the person behind it is verifiably real. That's the whole product.\n\nbidorah.com",
    tiktok:
      "Every seller on Bidorah is ID-verified. Actual humans, actual accountability. 🛡️\n\n#fyp #livestreamshopping #collectors #reseller",
    x:
      "On Bidorah, every seller is US-ID-verified. Real names. Real accountability. Scams get tossed in minutes, not weeks.\n\nbidorah.com",
  },
  "Plus launch": {
    instagram:
      "Introducing Bidorah Plus.\n\n• 1% cashback in credits on every purchase\n• 2× streak rewards\n• Early access to scheduled streams\n• Plus pill on your profile\n• Priority support\n\n$29.99/month. Or $299/year (17% off).\n\nCancel anytime. First purchase basically refunds itself.\n\nbidorah.com/plus 👑",
    tiktok:
      "Bidorah Plus dropped.\n\n1% cashback. 2× streaks. Early stream access. $29.99/mo or $299/yr. 👑\n\nbidorah.com/plus\n\n#fyp #livestreamshopping #subscription",
    x:
      "Bidorah Plus is live.\n\n1% cashback credits. 2× streak rewards. Early stream access. Plus pill.\n\n$29.99/mo · $299/yr (17% off)\n\nbidorah.com/plus",
  },
  "Utility — Price tracker": {
    instagram:
      "Know what it's worth. Before you bid.\n\nWe just shipped a live category-by-category price tracker. 12-week moving average. Updated every few minutes off actual Bidorah sales.\n\nSneakers, crystals, TCG, fragrances, luxury bags, storage unit finds — if it sold here, it's in the chart.\n\nFinally: no more guessing whether $280 is a steal or a scam.\n\nbidorah.com/prices",
    tiktok:
      "New on Bidorah: a live price tracker. See the 12-week average sale price for any category before you bid. 📊\n\nbidorah.com/prices\n\n#fyp #collectors #sneakerheads #reseller",
    x:
      "Bidorah Price Tracker is live.\n\n12-week rolling average sale price per category. Actual sold data. Updated every few minutes.\n\nbidorah.com/prices",
  },
};

/* ───────────────────────────────────────────────────────────────────────── */

export default function Brand() {
  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="brand-page">
      <Navbar />
      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-12">
        <p className="overline text-zinc-500">BIDORAH · BRAND & SOCIAL KIT</p>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">
          Logos, assets, captions — ready to post.
        </h1>
        <p className="mt-3 text-zinc-600 max-w-2xl">
          Right-click any image and choose <em>Save Image As</em> (or tap the download button). Captions are copy-paste ready per platform.
        </p>

        {/* Profile pictures */}
        <Section title="Profile pictures" subtitle="Use these on IG, TikTok, X, Facebook, LinkedIn, Discord. Squircle fits all — the circle version is pre-cropped for platforms that clip poorly.">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {PROFILE_PICS.map((p) => (
              <AssetCard key={p.src} {...p} small />
            ))}
          </div>
        </Section>

        {/* Share card */}
        <Section title="Link share card" subtitle="Auto-used when someone shares bidorah.com on Twitter, iMessage, Slack, Discord.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {SHARE_CARDS.map((p) => (
              <AssetCard key={p.src} {...p} />
            ))}
          </div>
        </Section>

        {/* Launch posts */}
        <Section title="Launch posts · 1080×1080" subtitle="6 square tiles for Instagram feed, LinkedIn, X image posts. Each one pairs with a caption set below.">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            {POSTS.map((p) => (
              <AssetCard key={p.src} {...p} />
            ))}
          </div>
        </Section>

        {/* Custom marketing mockups */}
        <Section
          title="Marketing mockups · multi-format"
          subtitle="Six campaign-ready mockups with the new flat-squircle Bidorah identity baked in. Phone-mockup live-stream UI rendered procedurally so they re-export instantly any time the brand tweaks."
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {MOCKUPS.map((m) => (
              <AssetCard key={m.src} {...m} tall={m.src.endsWith("05_story_poster.png")} />
            ))}
          </div>
        </Section>

        {/* Stories */}
        <Section title="Stories & Reels covers · 1080×1920" subtitle="Drop these on Instagram Stories, TikTok, YouTube Shorts as thumbnail/cover frames.">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            {STORIES.map((p) => (
              <AssetCard key={p.src} {...p} tall />
            ))}
          </div>
        </Section>

        {/* Reels */}
        <Section
          title="Reels & video ads · 1080×1920 MP4"
          subtitle="Ready to upload to Instagram Reels, TikTok, YouTube Shorts. H.264 / yuv420p — max platform compatibility. Tap play to preview."
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {REELS.map((r) => (
              <ReelCard key={r.src} {...r} />
            ))}
          </div>
        </Section>

        {/* Captions */}
        <Section
          title="Copy-paste captions"
          subtitle="Written for Instagram, TikTok, and X/Twitter voice. Tap copy and post."
        >
          <div className="space-y-6">
            {Object.entries(CAPTIONS).map(([key, v]) => (
              <CaptionCard key={key} title={key} variants={v} />
            ))}
          </div>
        </Section>

        {/* 30-day playbook */}
        <Section title="30-day launch content playbook" subtitle="Drop these in this order, 3 posts/week, across IG + TikTok + X.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              ["Day 01", "Hero: Bid live. Win real. (01)"],
              ["Day 03", "Trust: Every seller verified (02)"],
              ["Day 05", "Scarcity: 250 founder seats (03)"],
              ["Day 08", "Moat: Wholesale or retail (04)"],
              ["Day 10", "UGC: Re-share a founder seller's live show"],
              ["Day 12", "Behind-the-scenes: founder dashboard screenshot"],
              ["Day 15", "Utility: Price tracker demo (06)"],
              ["Day 17", "Plus launch (05) + founder offer"],
              ["Day 20", "User win: \"I bid $30 on a $300 Pikachu\""],
              ["Day 22", "Mini doc: What 'verified' actually means"],
              ["Day 25", "Remind: 250 seats → how many left?"],
              ["Day 27", "Seller testimonial reel"],
              ["Day 30", "Urgency: Founder applications close in 48h"],
            ].map(([d, t]) => (
              <div key={d} className="flex gap-3 items-center border border-zinc-200 rounded-xl p-3 bg-white">
                <span className="text-[10px] font-mono font-bold tracking-widest bg-zinc-900 text-white rounded px-2 py-1">
                  {d.toUpperCase()}
                </span>
                <span className="text-sm text-zinc-700">{t}</span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Hashtag bank" subtitle="Mix & match for each post — 5–10 hashtags per IG post is the sweet spot.">
          <div className="flex flex-wrap gap-2 text-sm">
            {[
              "#livestreamshopping",
              "#liveshopping",
              "#collectorsoftiktok",
              "#liveauction",
              "#auctions",
              "#sneakerheads",
              "#sneakerreseller",
              "#reseller",
              "#resellercommunity",
              "#tcgcollector",
              "#crystalcollector",
              "#fragrancecommunity",
              "#luxurybags",
              "#storageunitfinds",
              "#foundermember",
              "#foundingseller",
              "#livestream",
              "#dropshop",
              "#bidorah",
              "#thrift",
              "#smallbusiness",
              "#wholesale",
              "#wholesaledeals",
              "#liveshoppingapp",
            ].map((h) => (
              <span key={h} className="rounded-full border border-zinc-300 px-3 py-1 text-zinc-700">
                {h}
              </span>
            ))}
          </div>
        </Section>
      </main>
      <Footer />
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────────── */

function Section({ title, subtitle, children }) {
  return (
    <section className="mt-14">
      <p className="overline text-zinc-500">{title.toUpperCase()}</p>
      <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tighter mt-1">
        {title}
      </h2>
      {subtitle && <p className="text-sm text-zinc-600 mt-1 max-w-2xl">{subtitle}</p>}
      <div className="mt-6">{children}</div>
    </section>
  );
}

function ReelCard({ src, poster, label, hint, duration }) {
  const downloadName = src.split("/").pop();
  return (
    <div
      className="rounded-2xl border border-zinc-200 overflow-hidden bg-white hover:shadow-lg transition"
      data-testid={`reel-${downloadName}`}
    >
      <div className="aspect-[9/16] bg-zinc-900 overflow-hidden relative">
        <video
          src={src}
          poster={poster}
          controls
          playsInline
          muted
          preload="metadata"
          className="h-full w-full object-cover"
        />
        <span className="absolute top-2 right-2 bg-black/60 text-white text-[10px] font-bold tracking-widest rounded px-2 py-0.5 pointer-events-none">
          {duration}
        </span>
      </div>
      <div className="p-3 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">{label}</p>
          {hint && <p className="text-[11px] text-zinc-500 truncate">{hint}</p>}
        </div>
        <a
          href={src}
          download={downloadName}
          className="flex-shrink-0 rounded-full bg-zinc-900 text-white px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 hover:bg-black"
          data-testid={`download-${downloadName}`}
        >
          <DownloadSimpleIcon size={14} weight="bold" /> Save
        </a>
      </div>
    </div>
  );
}

function AssetCard({ src, label, hint, small, tall }) {
  const aspect = tall ? "aspect-[9/16]" : "aspect-square";
  const downloadName = src.split("/").pop();
  return (
    <div
      className="rounded-2xl border border-zinc-200 overflow-hidden bg-white hover:shadow-lg transition"
      data-testid={`asset-${downloadName}`}
    >
      <div className={`${aspect} bg-zinc-100 overflow-hidden`}>
        <img src={src} alt={label} className="h-full w-full object-cover" loading="lazy" />
      </div>
      <div className="p-3 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className={`${small ? "text-xs" : "text-sm"} font-semibold truncate`}>{label}</p>
          {hint && <p className="text-[11px] text-zinc-500 truncate">{hint}</p>}
        </div>
        <a
          href={src}
          download={downloadName}
          className="flex-shrink-0 rounded-full bg-zinc-900 text-white px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 hover:bg-black"
          data-testid={`download-${downloadName}`}
        >
          <DownloadSimpleIcon size={14} weight="bold" /> Save
        </a>
      </div>
    </div>
  );
}

function CaptionCard({ title, variants }) {
  return (
    <div className="rounded-2xl border border-zinc-200 overflow-hidden">
      <div className="bg-zinc-50 border-b border-zinc-200 px-5 py-3 font-display font-black text-lg">
        {title}
      </div>
      <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-5">
        {Object.entries(variants).map(([platform, text]) => (
          <CaptionBlock key={platform} platform={platform} text={text} />
        ))}
      </div>
    </div>
  );
}

function CaptionBlock({ platform, text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <div className="rounded-xl bg-zinc-50 border border-zinc-200 p-3 flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] tracking-[0.22em] uppercase font-black text-zinc-500">
          {platform}
        </p>
        <button
          onClick={copy}
          className="inline-flex items-center gap-1 text-[11px] font-bold text-[#1D78AB] hover:text-[#0E3F5C]"
          data-testid={`copy-${platform}`}
        >
          {copied ? <CheckIcon size={12} weight="bold" /> : <ClipboardTextIcon size={12} weight="bold" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="text-[12px] text-zinc-700 whitespace-pre-wrap font-sans leading-relaxed flex-1">
        {text}
      </pre>
    </div>
  );
}
